import { useNavigate, useSearchParams } from "react-router-dom";
import { ChevronRight, Clock, Mail } from "lucide-react";
import { useAuthStore } from "@/stores/auth-store";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AuthCard } from "@/components/shell/auth-card";
import type { Tenant } from "@/types";
// Mock-only, like mfa.tsx's hardcoded "wrong code" sentinel: this pre-login
// tenant switcher is not wired to a backend yet (the real login flow
// redirects through OIDC and never reaches this screen). Kept local to this
// page — not the shared auth store — so `data/auth-fixtures` stays out of
// the runtime session/org bootstrap path. Real API integration is tracked
// as separate follow-up page-delivery work.
import { AUTH_TENANTS as TENANTS } from "@/data/auth-fixtures";

export default function TenantSelectPage() {
  const selectTenant = useAuthStore((s) => s.selectTenant);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get("returnTo") ?? "/wallets";

  // Matches the canonical LOG-05 Figma frame: clicking a row (or one of its
  // environment pills) navigates immediately. There is no select-then-
  // enable-Continue gating.
  function goToTenant(tenant: Tenant) {
    selectTenant(tenant);
    navigate(returnTo);
  }

  return (
    <AuthCard>
      <div className="auth-icon" aria-hidden="true">
        <div className="auth-icon__inner">
          <Mail size={28} strokeWidth={1.25} />
        </div>
      </div>

      <div className="flex w-full flex-col gap-6">
        <div className="auth-heading">
          <h1 className="auth-title">Choose where to go</h1>
          <p className="auth-subtitle">Select a tenant and environment to continue</p>
        </div>

        <ul className="flex w-full flex-col gap-2.5">
          {TENANTS.map((tenant) => (
            <li key={tenant.id}>
              <div
                role="link"
                tabIndex={0}
                onClick={() => goToTenant(tenant)}
                onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && goToTenant(tenant)}
                className="flex w-full cursor-pointer items-center gap-3.5 rounded-lg border border-line bg-surface px-4 py-3.5 text-left outline-none transition-colors hover:border-primary focus-visible:ring-2 focus-visible:ring-primary-soft"
              >
                <span
                  aria-hidden
                  className="grid size-10 shrink-0 place-items-center rounded-full text-sm font-bold text-white"
                  style={{ background: tenant.bgColor }}
                >
                  {tenant.initials}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold text-ink">{tenant.name}</span>
                  <span className="mt-0.5 flex items-center gap-1.5 text-xs text-muted">
                    <Clock className="size-3" aria-hidden />
                    {tenant.roleLabel} · Last used {tenant.lastUsed}
                  </span>
                </span>
                <span className="flex flex-wrap justify-end gap-1">
                  {tenant.envs.map((env) => (
                    <button
                      key={env.name}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        goToTenant(tenant);
                      }}
                      className="outline-none"
                    >
                      <Badge variant={env.production ? "danger" : "neutral"} dot={false}>
                        {env.name}
                      </Badge>
                    </button>
                  ))}
                </span>
                <ChevronRight aria-hidden className="size-4 shrink-0 text-subtle" />
              </div>
            </li>
          ))}
        </ul>

        <Button size="lg" className="w-full" onClick={() => goToTenant(TENANTS[0])}>
          Continue
        </Button>
      </div>
    </AuthCard>
  );
}
